import React, { useState, useMemo, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import { Button, Grid, IconButton, useMediaQuery } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import Modal from "Components/Common/Modal";
import Backdrop from "Components/Common/Backdrop";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import Tooltip from "Components/Common/Tooltip";
import { BillingCycleViewModal } from "./BillingCycleViewModal";
import BillingCycleForm from "./BillingCycleForm";
import FPPPAChargesForm from "../BillMeterReading/FPPPAChargesForm";
import { getOptionLabel, getOptionValue } from "common/enums";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { useGetBillingCyclesQuery } from "apis/BillingCycle";
import { usePostGenerateSecurityDepositMutation } from "apis/SecurityDeposit";
import { usePostGenerateBackupPowerMutation } from "apis/BackupPower";
import { usePostGenerateBillsMutation } from "apis/Billing";
import { useLazyGetFpppaChargesByBillCycleId } from "apis/MeterReading";

import { GenerateBillIcon } from "./style";
import { useSelector } from "react-redux";
import { POWER_READ_ADMIN } from "common/constant";

const closedBillingCycleStatus = getOptionValue("billingStatus", "Closed");

const inProgressBillingCycleStatus = getOptionValue(
  "billingStatus",
  "In Progress"
);

const BillingCycle = ({ t }) => {
  const {
    data: { data: { items: billingCyclesData } = {} } = {},
    isLoading: isBillingCycleDataLoading,
  } = useGetBillingCyclesQuery();

  const [generateBills, { isLoading, isFetching }] =
    usePostGenerateBillsMutation();

  const [generateSecurityDeposit, { isLoading: isSecurityDepositGenerating }] =
    usePostGenerateSecurityDepositMutation();

  const [getFPPPACharges, { data: { data: fpppaChargesData = [] } = {} }] =
    useLazyGetFpppaChargesByBillCycleId();

  const [
    generateBackupPowerBills,
    { isLoading: isBackupPowerBillsGenerating },
  ] = usePostGenerateBackupPowerMutation();

  const [selectedBillCycleId, setSelectedBillCycleId] = useState("");
  const [subscriptionType, setSubScriptionType] = useState("");
  const [isBillingFormModalOpen, setIsBillingFormModalOpen] = useState(false);
  const [isBillingViewModalOpen, setIsBillingViewModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isFPPPAModalOpen, setIsFPPPAModalOpen] = useState(false);
  const [fpppaCharges, setFpppaCharges] = useState({});

  const { roles } = useSelector((state) => state.Login);
  const isReadAdmin = roles.includes(POWER_READ_ADMIN);

  const sm = useMediaQuery("(max-width: 599px)");

  const columns = useMemo(
    () => [
      {
        title: "Subscription Type",
        renderCell: ({ subscriptionType }) =>
          getOptionLabel("subscriptionTypes", subscriptionType),
      },
      {
        title: "Bill Cycle",
        renderCell: ({ billCycle }) =>
          moment(billCycle).isValid()
            ? convertUTCDateToLocalDate(billCycle).format("MMM-YYYY")
            : "N/A",
      },
      {
        title: "Status",
        renderCell: ({ status }) => getOptionLabel("billingStatus", status),
      },
      {
        title: "Charge Start Date",
        renderCell: ({ chargeStartDate }) =>
          moment(chargeStartDate).isValid()
            ? convertUTCDateToLocalDate(chargeStartDate).format("DD-MM-YYYY")
            : "N/A",
      },
      {
        title: "Charge End Date",
        renderCell: ({ chargeEndDate }) =>
          moment(chargeEndDate).isValid()
            ? convertUTCDateToLocalDate(chargeEndDate).format("DD-MM-YYYY")
            : "N/A",
      },
      {
        title: "Actions",
        renderCell: ({ id, status, subscriptionType }) => {
          const isEditDisabled = roles.includes(POWER_READ_ADMIN);
          return (
            <>
              <IconButton
                onClick={() => {
                  setSelectedBillCycleId(id);
                  setIsBillingViewModalOpen(true);
                }}
              >
                <Tooltip title={t("View Billing Cycle Details")}>
                  <RemoveRedEyeIcon
                    fontSize="small"
                    color="primary"
                    className="cursor-pointer"
                  />
                </Tooltip>
              </IconButton>
              {!isEditDisabled && (
                <IconButton
                  onClick={() => {
                    setSelectedBillCycleId(id);
                    setIsBillingFormModalOpen(true);
                  }}
                  disabled={status === closedBillingCycleStatus}
                >
                  <Tooltip title={t("Edit Billing Cycle")}>
                    <EditIcon
                      fontSize="small"
                      color={
                        status === closedBillingCycleStatus
                          ? "disabled"
                          : "primary"
                      }
                      className="cursor-pointer"
                    />
                  </Tooltip>
                </IconButton>
              )}
              {!isEditDisabled && (
                <IconButton
                  onClick={() => {
                    setSubScriptionType(subscriptionType);
                    setSelectedBillCycleId(id);
                    if (
                      getOptionValue("subscriptionTypes", "Electricity") ===
                      subscriptionType
                    ) {
                      setIsFPPPAModalOpen(true);
                    } else {
                      setIsConfirmationModalOpen(true);
                    }
                  }}
                  disabled={status === inProgressBillingCycleStatus}
                >
                  <Tooltip title={t("Calculate Bills")}>
                    <GenerateBillIcon
                      className="cursor-pointer"
                      disabled={status === inProgressBillingCycleStatus}
                    />
                  </Tooltip>
                </IconButton>
              )}
            </>
          );
        },
      },
    ],
    [billingCyclesData]
  );

  const isSubscriptionTypeElectricity = useMemo(
    () =>
      getOptionValue("subscriptionTypes", "Electricity") === subscriptionType,
    [subscriptionType]
  );

  const generateBillBtnHandler = async () => {
    try {
      if (
        subscriptionType === getOptionValue("subscriptionTypes", "Backup Power")
      ) {
        await generateBackupPowerBills(selectedBillCycleId);
      } else if (
        subscriptionType ===
        getOptionValue("subscriptionTypes", "Security Deposit")
      ) {
        await generateSecurityDeposit(selectedBillCycleId);
      } else {
        const charges = isSubscriptionTypeElectricity ? fpppaCharges : [];
        await generateBills(selectedBillCycleId, {}, charges);
      }
    } catch (err) {}
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { fpppaChargesData },
    validationSchema: Yup.object({
      fpppaChargesData: Yup.array().of(
        Yup.object().shape({
          charges: Yup.number()
            .typeError("Charges must be a number")
            .required("Charges is required"),
        })
      ),
    }),
    onSubmit: async (values) => {
      setFpppaCharges(values?.fpppaChargesData);
      setIsConfirmationModalOpen(true);
    },
  });

  useEffect(() => {
    (async () => {
      if (isFPPPAModalOpen && selectedBillCycleId) {
        try {
          await getFPPPACharges(selectedBillCycleId);
        } catch (err) {}
      }
    })();
  }, [isFPPPAModalOpen]);

  const { values, handleSubmit, resetForm } = formik;

  return (
    <>
      <BreadCrumb title={t("Billing Cycle")} pageTitle={t("Billing")} />
      <Backdrop open={isLoading || isFetching || isBillingCycleDataLoading} />
      {!isReadAdmin && (
        <Row>
          <Col md={3} className="w-100 d-flex justify-content-end">
            <Button
              variant="contained"
              className=" mb-2"
              size="medium"
              onClick={() => setIsBillingFormModalOpen(true)}
              disabled={isBillingCycleDataLoading}
            >
              {t("Add")}
            </Button>
          </Col>
        </Row>
      )}

      {!!isFPPPAModalOpen && (
        <Modal
          isOpen
          title="Variable Fuel Surcharges"
          maxWidth="lg"
          handleClose={() => {
            setSelectedBillCycleId("");
            setSubScriptionType("");
            setIsFPPPAModalOpen(false);
            resetForm();
          }}
        >
          <Grid
            container
            columnSpacing={1}
            className="border border-1 pt-2 pe-2 mb-2"
          >
            <FPPPAChargesForm
              fppaChargesData={values?.fpppaChargesData ?? []}
              formik={formik}
            />
          </Grid>
          <div className="text-end">
            <Button
              variant="contained"
              className="mt-2"
              disabled={isConfirmationModalOpen}
              onClick={handleSubmit}
            >
              {t("Submit")}
            </Button>
          </div>
        </Modal>
      )}

      {isConfirmationModalOpen && (
        <ConfirmationDialog
          message="Are you sure you want to generate bills for this cycle?"
          onCancel={() => {
            setIsConfirmationModalOpen(false);
            setSelectedBillCycleId("");
            setSubScriptionType("");
            setIsFPPPAModalOpen(false);
            resetForm();
          }}
          isLoading={
            isSecurityDepositGenerating ||
            isLoading ||
            isFetching ||
            isBackupPowerBillsGenerating
          }
          onConfirm={async () => {
            await generateBillBtnHandler();
            setIsConfirmationModalOpen(false);
            setSelectedBillCycleId("");
            setSubScriptionType("");
            setIsFPPPAModalOpen(false);
            resetForm();
          }}
          isOpen={isConfirmationModalOpen}
          title="Warning"
          variant="warning"
        />
      )}
      {isBillingViewModalOpen && (
        <Modal
          isOpen={isBillingViewModalOpen}
          handleClose={() => {
            setIsBillingViewModalOpen(false);
            setSelectedBillCycleId("");
          }}
          title="View Billing Cycle Form"
          maxWidth="md"
        >
          <BillingCycleViewModal
            selectedBillCycleId={selectedBillCycleId}
            t={t}
          />
        </Modal>
      )}
      {isBillingFormModalOpen && (
        <Modal
          isOpen={isBillingFormModalOpen}
          handleClose={() => {
            setIsBillingFormModalOpen(false);
            setSelectedBillCycleId("");
          }}
          title={`${selectedBillCycleId ? "Edit" : "Add"} Billing Cycle`}
          maxWidth="md"
        >
          <BillingCycleForm
            selectedBillCycleId={selectedBillCycleId}
            handleClose={() => {
              setIsBillingFormModalOpen(false);
              setSelectedBillCycleId("");
            }}
          />
        </Modal>
      )}
      <BaseTable
        pagination={false}
        data={billingCyclesData || []}
        columns={columns}
        pageSize={10}
        maxHeight={sm ? "calc(100vh - 350px)" : "calc(100vh - 250px)"}
      />
    </>
  );
};

BillingCycle.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(BillingCycle);
